import './assets/css/App.css';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { publicRoutes } from 'routes';
import { Box, Button, ChakraProvider, HStack, Text } from '@chakra-ui/react';
import PushNotificationPrompt from 'components/notifications/PushNotificationPrompt';
import CookieConsentBanner from 'components/notifications/CookieConsentBanner';
import PWAInstallCoachmark from 'components/notifications/PWAInstallCoachmark';
import DocumentOverscrollBackground from 'components/DocumentOverscrollBackground';
import { orgcloudGlassToastOptions } from 'components/notifications/glassToastDefaults';
import { isPushPromptDeclinedInCookie } from 'utils/pushNotificationBannerCookie';
import { hasCookieConsentRecorded, recordCookieConsent } from 'utils/cookieConsentStorage';
import initialTheme from './theme/theme';
import React, { Suspense, lazy, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useUserContext, useBrandContext, useCompanyContext } from './services/appContext';
import TokenManager from 'services/tokenManager';
import { diagnosticSourceFile, safeErrorCode, safeErrorDiagnostic } from 'services/errorDiagnostics';
import { idTokenCalendarExpired, idTokenWithinRefreshWindow } from 'utils/sessionTokenPolicy';

import ApiService from 'services/ApiService';
import FrontendAnalyticsService, {
  buildFrontendRuntimeProperties,
  buildPageViewAnalyticsProperties,
  compactAnalyticsError,
  describeFormSubmission,
  describeInteractionTarget,
} from 'services/frontendAnalytics';
import Favicon from 'assets/img/orgcloud/favicon.png';
import usePushSubscription, { subscribeToPush } from 'services/usePushSubscription';
import PWASplashScreen from 'components/PWASplashScreen';
import { buildManifestData, updateManifestLink, updateFaviconLink, ensureAppleTouchIcon, versionAssetUrl } from 'services/pwaBranding';
import { isMainDomain, getTenantRoutingIdentifier, isLocalDevelopmentHost } from 'utils/domainUtils';
import { normalizeLegacyAdminSectionPath } from 'utils/routePaths';
import AppRouteSkeleton from 'components/auth/AppRouteSkeleton';
import { isInstalledAppRuntime } from 'utils/pwaRuntime';

const AuthLayout = lazy(() => import('./layouts/auth'));
const AdminLayout = lazy(() => import('./layouts/admin'));
const PublicLayout = lazy(() => import('layouts/main'));
const RTLLayout = lazy(() => import('./layouts/rtl'));
const PublicPurchaseReceipt = lazy(() => import('views/public/receipt/PublicPurchaseReceipt'));
const PublicReservations = lazy(() => import('views/public/reservations'));
const PublicMemberProfile = lazy(() => import('views/public/profile/PublicMemberProfile'));
const PublicSportEventPage = lazy(() => import('views/public/events/PublicSportEventPage'));

const isAuthPath = (pathname: string, hash: string = '') =>
  pathname.startsWith('/auth/') ||
  hash.startsWith('#/auth/') ||
  window.location.hash.startsWith('#/auth/');

const isPublicPath = (pathname: string, hash: string = '') =>
  pathname.startsWith('/public/') ||
  pathname.startsWith('/event/') ||
  pathname.startsWith('/e/') ||
  pathname.startsWith('/competition/') ||
  pathname.startsWith('/c/') ||
  pathname.startsWith('/r/') ||
  pathname.startsWith('/club/') ||
  pathname.startsWith('/instructor/') ||
  pathname.startsWith('/ticket/') ||
  pathname.startsWith('/profile/') ||
  pathname.startsWith('/p/') ||
  hash.startsWith('#/public/') ||
  hash.startsWith('#/event/') ||
  hash.startsWith('#/e/') ||
  hash.startsWith('#/competition/') ||
  hash.startsWith('#/c/') ||
  hash.startsWith('#/r/') ||
  hash.startsWith('#/club/') ||
  hash.startsWith('#/instructor/') ||
  hash.startsWith('#/ticket/') ||
  hash.startsWith('#/profile/') ||
  hash.startsWith('#/p/') ||
  window.location.hash.startsWith('#/public/') ||
  window.location.hash.startsWith('#/event/') ||
  window.location.hash.startsWith('#/e/') ||
  window.location.hash.startsWith('#/competition/') ||
  window.location.hash.startsWith('#/c/') ||
  window.location.hash.startsWith('#/r/') ||
  window.location.hash.startsWith('#/club/') ||
  window.location.hash.startsWith('#/instructor/') ||
  window.location.hash.startsWith('#/ticket/') ||
  window.location.hash.startsWith('#/profile/') ||
  window.location.hash.startsWith('#/p/');

const isProtectedAppPath = (pathname: string, hash: string = '') => {
  const hashPath = hash.startsWith('#') ? hash.substring(1) : hash;
  const windowHashPath = window.location.hash.startsWith('#') ? window.location.hash.substring(1) : window.location.hash;
  return [pathname, hashPath, windowHashPath].some((candidate) =>
    candidate === '/admin' ||
    candidate.startsWith('/admin/') ||
    candidate === '/pages' ||
    candidate.startsWith('/pages/') ||
    candidate === '/objects' ||
    candidate.startsWith('/objects/') ||
    candidate === '/tools' ||
    candidate.startsWith('/tools/') ||
    candidate === '/rtl' ||
    candidate.startsWith('/rtl/')
  );
};

const getUsableStoredIdToken = () => {
  const token = TokenManager.getIdToken();
  if (!token) return '';

  try {
    if (token.split('.').length !== 3) {
      TokenManager.clearSession();
      return '';
    }

    const payload = JSON.parse(window.atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))) as Partial<TokenData>;
    const exp = Number(payload.exp || 0);
    if (!exp || idTokenCalendarExpired(exp, Math.floor(Date.now() / 1000))) {
      if (!TokenManager.shouldAttemptRefresh()) {
        TokenManager.clearSession();
        return '';
      }
    }
    return token;
  } catch {
    TokenManager.clearSession();
    return '';
  }
};

type TenantBootstrapPayload = {
  brandingResponse: any | null;
  landingPage: LandingPageConfig | null;
};

const tenantBootstrapCache = new Map<string, Promise<TenantBootstrapPayload>>();

const readTenantBootstrap = (tenantIdentifier: string): Promise<TenantBootstrapPayload> => {
  const cacheKey = String(tenantIdentifier || '').trim().toLowerCase();
  if (!cacheKey) {
    return Promise.resolve({ brandingResponse: null, landingPage: null });
  }
  const cached = tenantBootstrapCache.get(cacheKey);
  if (cached) return cached;

  const request = Promise.all([
    ApiService.readBranding(cacheKey, true, false).catch(() => null),
    ApiService.readLandingPage(cacheKey),
  ]).then(([brandingResponse, landingPage]) => ({
    brandingResponse,
    landingPage,
  }));

  tenantBootstrapCache.set(cacheKey, request);
  return request;
};

// Componente wrapper que evita ejecutar LandingRedirect para rutas públicas
function ConditionalLandingRedirect() {
  const pathname = window.location.pathname;
  const hash = window.location.hash;

  if (isPublicPath(pathname, hash) || isAuthPath(pathname, hash)) {
    console.log("ConditionalLandingRedirect: public/auth route, skipping landing redirect:");
    return null;
  }

  if (isProtectedAppPath(pathname, hash)) {
    return getUsableStoredIdToken() ? null : <Navigate to="/auth/sign-in/" replace />;
  }

  // Si estamos en una ruta pública o de auth, no hacer nada
  // Las rutas públicas deben ser accesibles sin ninguna interferencia
  if (isPublicPath(pathname, hash) || pathname.startsWith('/auth/')) {
    console.log("✅ ConditionalLandingRedirect: Ruta pública o auth detectada, no renderizando LandingRedirect:");
    return null;
  }
  
  return <LandingRedirect />;
}

function LegacyAdminSectionRedirect() {
  const location = useLocation();
  const targetPath = normalizeLegacyAdminSectionPath(location.pathname);

  return <Navigate to={`${targetPath}${location.search}${location.hash}`} replace />;
}

function LegacyAppRedirect({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

function LegacyPagesPageRedirect() {
  const location = useLocation();
  const targetPath = location.pathname.replace(/^\/pages\/page(?=\/|$)/, '/pages');
  return <Navigate to={`${targetPath}${location.search}${location.hash}`} replace />;
}

function ScrollToTopOnRouteChange(): React.ReactElement | null {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, location.search]);

  return null;
}

// Componente para redirigir a landing o login según exista landing page
// Este componente se ejecuta ANTES de renderizar cualquier cosa, evitando el flash del login
function LandingRedirect(): React.ReactElement | null {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [, setBrandName] = useState<string>('');

  useEffect(() => {
    const checkLanding = async () => {
      // Si ya estamos en cualquier ruta /public/* o /auth/*, no hacer nada
      // Las rutas públicas (form, landing, etc.) deben ser accesibles sin redirección
      const pathname = location.pathname;
      const hash = window.location.hash;
      
      // Verificar tanto pathname como hash para rutas de auth
      // También verificar window.location.hash para casos donde React Router aún no ha procesado la ruta
      const isAuthRoute = isAuthPath(pathname, hash);
      const isPublicRoute = isPublicPath(pathname, hash);
      const isProtectedRoute = isProtectedAppPath(pathname, hash);
      
      if (isPublicRoute || isAuthRoute || isProtectedRoute) {
        console.log("✅ LandingRedirect: Ruta pública o auth detectada, no redirigiendo:");
        setIsChecking(false);
        return;
      }

      const hostname = window.location.hostname;
      // Extraer subdomain de forma más robusta
      // Para "bubloud.orgcloud.io" -> "bubloud"
      // Para "localhost" -> null
      const tenantIdentifier = getTenantRoutingIdentifier(hostname);
      
      console.log("🔍 LandingRedirect: Verificando landing page");
      
      // Cargar branding en paralelo para mostrar logo mientras se verifica
      if (tenantIdentifier) {
        try {
          // Cargar branding y landing page en paralelo
          const { brandingResponse, landingPage } = await readTenantBootstrap(tenantIdentifier);

          // Configurar logo y nombre del cliente
          if (brandingResponse) {
            const uniqueName = brandingResponse.uniqueName || brandingResponse.name || '';
            const capitalizedName = uniqueName.charAt(0).toUpperCase() + uniqueName.slice(1).toLowerCase();
            setBrandName(capitalizedName);
          }

          console.log("✅ LandingRedirect: Landing page encontrada:");
          
          // Si existe landing page y está Live, redirigir DIRECTAMENTE a ella
          if (landingPage && landingPage.isLive) {
            console.log('✅ LandingRedirect: Redirigiendo directamente a /public/landing (sin pasar por login)');
            navigate('/public/landing', { replace: true });
            setIsChecking(false);
            return;
          } else {
            // Si no está Live, redirigir a login
            console.log('⚠️ LandingRedirect: Landing page no está Live, redirigiendo a login');
            navigate('/auth/sign-in/', { replace: true });
          }
        } catch (error) {
          // Si no existe o hay error, intentar cargar branding de todas formas
          try {
            const brandingResponse = await ApiService.readBranding(tenantIdentifier, true, false);
            const uniqueName = brandingResponse.uniqueName || brandingResponse.name || '';
            const capitalizedName = uniqueName.charAt(0).toUpperCase() + uniqueName.slice(1).toLowerCase();
            setBrandName(capitalizedName);
          } catch (brandingError) {
            console.warn("No se pudo cargar branding:", safeErrorDiagnostic(brandingError));
          }
          
          // Redirigir a login
          console.error("❌ LandingRedirect: Error obteniendo landing page:", safeErrorDiagnostic(error));
          navigate('/auth/sign-in/', { replace: true });
        }
      } else {
        // Sin subdomain válido, redirigir a login
        console.log('⚠️ LandingRedirect: Sin subdomain válido, redirigiendo a login');
        navigate('/auth/sign-in/', { replace: true });
      }
      setIsChecking(false);
    };

    checkLanding();
  }, [navigate, location.hash, location.pathname]);

  // Si estamos en una ruta pública, no hacer nada (dejar que se renderice normalmente)
  const pathname = location.pathname;
  const hash = location.hash;
  if (isPublicPath(pathname, hash)) {
    return null; // Permitir que la ruta pública se renderice sin interferencia
  }

  if (isChecking) {
    return <AppRouteSkeleton />;
  }

  return null;
}

// Componente wrapper para verificar landing page antes de mostrar auth
// NO redirige si el usuario accede explícitamente a /auth/*
function AuthWithLandingCheck({ children }: { children: React.ReactElement }): React.ReactElement | null {
  const navigate = useNavigate();
  const location = useLocation();
  const isExplicitAuthRoute = isAuthPath(location.pathname, location.hash);
  const [isChecking, setIsChecking] = useState(() => !isExplicitAuthRoute);
  const [shouldShowAuth, setShouldShowAuth] = useState(() => isExplicitAuthRoute);
  const [, setBrandName] = useState<string>('');

  useEffect(() => {
    const checkLanding = async () => {
      const pathname = location.pathname;
      
      // Si el usuario accede explícitamente a /auth/*, NO redirigir
      // Permitir que vea el login normalmente
      if (isAuthPath(pathname, window.location.hash)) {
        console.log('✅ AuthWithLandingCheck: Acceso explícito a /auth/*, mostrando auth sin redirigir');
        setShouldShowAuth(true);
        setIsChecking(false);
        return;
      }

      const hostname = window.location.hostname;
      // Extraer subdomain de forma más robusta
      const tenantIdentifier = getTenantRoutingIdentifier(hostname);
      
      console.log("🔍 AuthWithLandingCheck: Verificando landing page");
      
      // Solo verificar si hay subdomain válido
      if (tenantIdentifier) {
        try {
          // Cargar branding y landing page en paralelo
          const { brandingResponse, landingPage } = await readTenantBootstrap(tenantIdentifier);

          // Configurar logo y nombre del cliente
          if (brandingResponse) {
            const uniqueName = brandingResponse.uniqueName || brandingResponse.name || '';
            const capitalizedName = uniqueName.charAt(0).toUpperCase() + uniqueName.slice(1).toLowerCase();
            setBrandName(capitalizedName);
          }

          console.log("🔍 AuthWithLandingCheck: Landing page encontrada:");
          
          // Si existe landing page y está Live, redirigir a ella
          if (landingPage && landingPage.isLive) {
            console.log('✅ AuthWithLandingCheck: Redirigiendo a /public/landing');
            navigate('/public/landing', { replace: true });
            return;
          } else {
            // Si no está Live, mostrar auth normalmente
            console.log('⚠️ AuthWithLandingCheck: Landing page no está Live, mostrando auth');
            setShouldShowAuth(true);
          }
        } catch (error) {
          // Si no existe landing page, intentar cargar branding de todas formas
          try {
            const brandingResponse = await ApiService.readBranding(tenantIdentifier, true, false);
            const uniqueName = brandingResponse.uniqueName || brandingResponse.name || '';
            const capitalizedName = uniqueName.charAt(0).toUpperCase() + uniqueName.slice(1).toLowerCase();
            setBrandName(capitalizedName);
          } catch (brandingError) {
            console.warn("No se pudo cargar branding:", safeErrorDiagnostic(brandingError));
          }
          
          // Mostrar auth normalmente
          console.error("❌ AuthWithLandingCheck: Error obteniendo landing page:", safeErrorDiagnostic(error));
          setShouldShowAuth(true);
        }
      } else {
        // Sin subdomain válido, mostrar auth normalmente
        setShouldShowAuth(true);
      }
      setIsChecking(false);
    };

    checkLanding();
  }, [navigate, location.pathname]);

  if (isChecking) {
    return <AppRouteSkeleton />;
  }

  if (!shouldShowAuth) {
    return <AppRouteSkeleton />;
  }

  return children;
}

interface TokenData {
  exp: number;
}

const SPLASH_SESSION_KEY = 'orgcloud_pwa_splash_seen';
const LEGACY_BRAND_ICON_B64_KEY = 'orgcloud_brand_icon_b64';
const LEGACY_BRAND_ICON_URL_KEY = 'orgcloud_brand_icon_url';
const LEGACY_BRAND_NAME_KEY = 'orgcloud_brand_name';
const LEGACY_BRAND_VERSION_KEY = 'orgcloud_branding_version';
const LEGACY_IOS_BRANDING_NOTICE_KEY = 'orgcloud_ios_branding_notice';

const normalizeBrandName = (value?: string | null) =>
  typeof value === 'string' ? value.trim() : '';

const resolveBrandStorageScope = () => {
  if (typeof window === 'undefined') return 'orgcloud';
  return String(window.location.hostname || 'orgcloud').trim().toLowerCase() || 'orgcloud';
};

const buildScopedBrandStorageKey = (baseKey: string, scope: string) =>
  `${baseKey}:${scope}`;

const getInitialStandalone = () => {
  return isInstalledAppRuntime();
};

const isMobileStandaloneSplashDevice = () => {
  if (typeof window === 'undefined') return false;
  try {
    const ua = String(window.navigator.userAgent || '');
    const isAppleMobile =
      /iPhone|iPad|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
    const isAndroidMobile = /Android/i.test(ua);
    if (!isAppleMobile && !isAndroidMobile) {
      return false;
    }
    const coarsePointer =
      window.matchMedia('(pointer: coarse)').matches ||
      window.matchMedia('(any-pointer: coarse)').matches;
    const hasTouchPoints = Number(window.navigator.maxTouchPoints || 0) > 0;
    const screenWidth = Number(window.screen?.width || window.innerWidth || 0);
    const screenHeight = Number(window.screen?.height || window.innerHeight || 0);
    const shortestSide = Math.min(screenWidth || Infinity, screenHeight || Infinity);
    return (coarsePointer || hasTouchPoints) && shortestSide <= 1024;
  } catch {
    return false;
  }
};

const getSplashSeenFlag = () => {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(SPLASH_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
};

const getInitialBootSplashActive = () => {
  if (typeof window === 'undefined') return false;
  return Boolean((window as any).__ORGCLOUD_BOOT_SPLASH_ACTIVE__);
};

const canInlineBrandImageForCurrentOrigin = (value?: string | null) => {
  if (typeof window === 'undefined') return false;
  const candidate = String(value || '').trim();
  if (!candidate || !canInlineBrandImage(candidate)) return false;
  try {
    const resolved = new URL(candidate, window.location.href);
    const current = new URL(window.location.href);
    if (resolved.origin !== current.origin && /^https?:$/i.test(resolved.protocol)) {
      return false;
    }
  } catch {
    return false;
  }
  return true;
};

const toBrandTitleCase = (value?: string | null) => {
  const normalized = normalizeBrandName(value);
  if (!normalized) return '';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const getTenantFallbackBrandInfo = () => {
  const tenantIdentifier = getTenantRoutingIdentifier();
  if (!tenantIdentifier) {
    return { icon: Favicon, name: 'OrgCloud' };
  }
  return {
    icon: '',
    name: toBrandTitleCase(tenantIdentifier),
  };
};

const canInlineBrandImage = (url?: string | null) => {
  const rawUrl = String(url || '').trim();
  if (!rawUrl) return false;
  if (/^(data:|blob:)/i.test(rawUrl)) return true;
  if (typeof window === 'undefined') return false;
  try {
    const parsed = new URL(rawUrl, window.location.origin);
    return parsed.origin === window.location.origin;
  } catch {
    return false;
  }
};

const isLocalPreviewHost = (): boolean =>
  typeof window !== 'undefined' && isLocalDevelopmentHost(window.location.hostname);

export default function Main() {
  const navigate = useNavigate(); // ✅ para redirección programática
  const location = useLocation();
  const [currentTheme, setCurrentTheme] = useState(initialTheme);
  const { user } = useUserContext();
  const { setFavicon, setBrandName } = useBrandContext();
  const [isExpired, setIsExpired] = useState<boolean | null>(null);
  /** Evita solapar varias llamadas a Cognito refreshSession desde este árbol. */
  const sessionRefreshInFlightRef = useRef(false);
  const { id } = useCompanyContext();
  const isLocalPreview = useMemo(() => isLocalPreviewHost(), []);

  const [showBanner, setShowBanner] = useState(false);
  const [cookieConsentOk, setCookieConsentOk] = useState(() => isLocalPreviewHost() || hasCookieConsentRecorded());
  const privacyPolicyUrl = process.env.REACT_APP_PRIVACY_POLICY_URL || undefined;
  const brandStorageScope = useMemo(() => resolveBrandStorageScope(), []);
  const BRAND_ICON_B64_KEY = useMemo(
    () => buildScopedBrandStorageKey(LEGACY_BRAND_ICON_B64_KEY, brandStorageScope),
    [brandStorageScope]
  );
  const BRAND_ICON_URL_KEY = useMemo(
    () => buildScopedBrandStorageKey(LEGACY_BRAND_ICON_URL_KEY, brandStorageScope),
    [brandStorageScope]
  );
  const BRAND_NAME_KEY = useMemo(
    () => buildScopedBrandStorageKey(LEGACY_BRAND_NAME_KEY, brandStorageScope),
    [brandStorageScope]
  );
  const BRAND_VERSION_KEY = useMemo(
    () => buildScopedBrandStorageKey(LEGACY_BRAND_VERSION_KEY, brandStorageScope),
    [brandStorageScope]
  );
  const IOS_BRANDING_NOTICE_KEY = useMemo(
    () => buildScopedBrandStorageKey(LEGACY_IOS_BRANDING_NOTICE_KEY, brandStorageScope),
    [brandStorageScope]
  );

  const getStoredBrandInfo = () => {
    if (typeof window === 'undefined') {
      return { icon: Favicon, name: 'OrgCloud' };
    }
    const tenantFallback = getTenantFallbackBrandInfo();
    const isTenantHost = Boolean(getTenantRoutingIdentifier());
    try {
      const storedIcon =
        localStorage.getItem(BRAND_ICON_B64_KEY) ||
        localStorage.getItem(BRAND_ICON_URL_KEY) ||
        tenantFallback.icon ||
        (isTenantHost ? '' : Favicon);
      const storedName =
        normalizeBrandName(localStorage.getItem(BRAND_NAME_KEY)) ||
        tenantFallback.name ||
        'OrgCloud';
      return { icon: storedIcon, name: storedName };
    } catch {
      return tenantFallback;
    }
  };

  const [brandInfo, setBrandInfo] = useState(() => getStoredBrandInfo());
  const [brandingVersion, setBrandingVersion] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    try {
      return localStorage.getItem(BRAND_VERSION_KEY) || '';
    } catch {
      return '';
    }
  });
  const [isBrandLoading, setIsBrandLoading] = useState(true);
  const initialStandalone = getInitialStandalone();
  const initialSplashSeen = getSplashSeenFlag();
  const initialMobileStandaloneSplash = isMobileStandaloneSplashDevice();
  const initialBootSplashActive = getInitialBootSplashActive();
  const [hasSeenSplash, setHasSeenSplash] = useState(initialSplashSeen);
  const [showSplash, setShowSplash] = useState(
    initialStandalone && initialMobileStandaloneSplash && !initialSplashSeen && !initialBootSplashActive
  );
  const [bootSplashActive, setBootSplashActive] = useState(initialBootSplashActive);
  const [installPromptEvent, setInstallPromptEvent] = useState<any>(null);
  const [isStandalone, setIsStandalone] = useState(initialStandalone);
  const [isMobileStandaloneExperience, setIsMobileStandaloneExperience] = useState(
    initialMobileStandaloneSplash
  );
  const [showIosBrandingNotice, setShowIosBrandingNotice] = useState(false);
  const lastTrackedPageViewRef = useRef('');
  const lastInteractionSignatureRef = useRef('');
  const lastInteractionTimestampRef = useRef(0);
  const routeVisibleStartedAtRef = useRef<number | null>(null);
  const routeAccumulatedVisibleMsRef = useRef(0);
  const routeInteractionCountRef = useRef(0);
  const routeScrollMilestonesRef = useRef<Set<number>>(new Set());
  const firstInteractionSentRef = useRef(false);
  const frontendSessionStartedRef = useRef(false);
  const isPublicAnalyticsRoute =
    location.pathname.startsWith('/auth/') ||
    isPublicPath(location.pathname, location.hash) ||
    location.pathname === '/';
  const brandingVersionRef = useRef<string>(
    typeof window === 'undefined'
      ? ''
      : (() => {
          try {
            return localStorage.getItem(BRAND_VERSION_KEY) || '';
          } catch {
            return '';
          }
        })()
  );

  const isIosDevice = useCallback(() => {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent || '';
    return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
  }, []);

  const iosBrandingNoticeCopy = useMemo(() => {
    const rawLanguage =
      (typeof document !== 'undefined' ? document.documentElement.lang : '') ||
      (typeof navigator !== 'undefined' ? navigator.language : '') ||
      'en';
    const language = rawLanguage.toLowerCase();

    if (language.startsWith('es')) {
      return {
        title: 'La identidad visual de esta app se ha actualizado',
        body:
          'En iPhone y iPad el icono o el nombre instalados pueden tardar en refrescarse. Si sigues viendo la versión antigua, cierra la app y vuelve a abrirla. Si aún no cambia, elimina la web app y añádela de nuevo desde Safari con "Abrir como app web".',
        action: 'Entendido',
      };
    }
    if (language.startsWith('fr')) {
      return {
        title: "L'identité visuelle de cette app a été mise à jour",
        body:
          "Sur iPhone et iPad, l'icône ou le nom installés peuvent mettre un peu de temps à se rafraîchir. Si vous voyez encore l'ancienne version, fermez l'app puis ouvrez-la à nouveau. Si cela ne change toujours pas, supprimez l'app web et ajoutez-la de nouveau depuis Safari avec \"Ouvrir comme app web\".",
        action: "J'ai compris",
      };
    }
    if (language.startsWith('pt')) {
      return {
        title: 'A identidade visual desta app foi atualizada',
        body:
          'No iPhone e no iPad, o ícone ou o nome instalados podem demorar a atualizar. Se continuares a ver a versão antiga, fecha a app e volta a abri-la. Se mesmo assim não mudar, remove a web app e adiciona-a novamente a partir do Safari com "Abrir como app web".',
        action: 'Percebi',
      };
    }
    if (language.startsWith('it')) {
      return {
        title: "L'identità visiva di questa app è stata aggiornata",
        body:
          "Su iPhone e iPad l'icona o il nome installati possono impiegare un po' ad aggiornarsi. Se vedi ancora la versione precedente, chiudi l'app e riaprila. Se non cambia ancora, rimuovi la web app e aggiungila di nuovo da Safari con \"Apri come web app\".",
        action: 'Ho capito',
      };
    }
    if (language.startsWith('de')) {
      return {
        title: 'Das Erscheinungsbild dieser App wurde aktualisiert',
        body:
          'Auf iPhone und iPad kann es etwas dauern, bis sich Symbol oder Name der installierten App aktualisieren. Wenn du noch die alte Version siehst, schließe die App und öffne sie erneut. Falls sie sich weiterhin nicht ändert, entferne die Web-App und füge sie in Safari mit „Als Web-App öffnen“ erneut hinzu.',
        action: 'Verstanden',
      };
    }

    return {
      title: 'This app branding has been updated',
      body:
        'On iPhone and iPad, the installed icon or app name can take a while to refresh. If you still see the old version, close the app and open it again. If it still does not update, remove the web app and add it again from Safari with "Open as Web App".',
      action: 'Got it',
    };
  }, []);

  const clearLegacyBrandingKeys = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem(LEGACY_BRAND_ICON_B64_KEY);
      localStorage.removeItem(LEGACY_BRAND_ICON_URL_KEY);
      localStorage.removeItem(LEGACY_BRAND_NAME_KEY);
      localStorage.removeItem(LEGACY_BRAND_VERSION_KEY);
      localStorage.removeItem(LEGACY_IOS_BRANDING_NOTICE_KEY);
    } catch {
      // ignore storage issues
    }
  }, []);

  const clearStoredBrandingVersion = useCallback(() => {
    if (typeof window === 'undefined') return;
    brandingVersionRef.current = '';
    setBrandingVersion('');
    try {
      localStorage.removeItem(BRAND_VERSION_KEY);
    } catch {
      // ignore storage issues
    }
  }, [BRAND_VERSION_KEY]);

  const cacheBrandAssets = useCallback(async (iconUrl: string, brandName: string, version?: string | null) => {
    if (typeof window === 'undefined') return;
    try {
      const safeName = normalizeBrandName(brandName) || 'OrgCloud';
      const versionedIconUrl = versionAssetUrl(iconUrl, version);
      clearLegacyBrandingKeys();
      localStorage.setItem(BRAND_NAME_KEY, safeName);
      localStorage.setItem(BRAND_ICON_URL_KEY, versionedIconUrl || iconUrl);
      if (version) {
        localStorage.setItem(BRAND_VERSION_KEY, version);
      }

      const inlineCandidate = versionedIconUrl || iconUrl;
      if (canInlineBrandImageForCurrentOrigin(inlineCandidate)) {
        await new Promise<void>((resolve) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              canvas.width = img.naturalWidth || 192;
              canvas.height = img.naturalHeight || 192;
              const ctx = canvas.getContext('2d');
              if (ctx) {
                ctx.drawImage(img, 0, 0);
                const dataUrl = canvas.toDataURL('image/png');
                localStorage.setItem(BRAND_ICON_B64_KEY, dataUrl);
              }
            } catch {
              // ignore canvas issues; the versioned URL is already cached
            }
            resolve();
          };
          img.onerror = () => resolve();
          img.src = inlineCandidate;
        });
      }
    } catch (error) {
      // No bloquear: el icono se mostrará desde la URL directamente
    }
  }, [BRAND_ICON_B64_KEY, BRAND_ICON_URL_KEY, BRAND_NAME_KEY, BRAND_VERSION_KEY, clearLegacyBrandingKeys]);

  const applyResolvedBranding = useCallback(async (
    response: any,
    options?: {
      allowSoftReload?: boolean;
      tenantIdentifier?: string;
    }
  ) => {
    const faviconUrl = response?.img || (options?.tenantIdentifier ? '' : Favicon);
    const uniqueName = response?.uniqueName || response?.name || 'OrgCloud';
    const resolvedBrandName = uniqueName.charAt(0).toUpperCase() + uniqueName.slice(1).toLowerCase();
    const nextBrandingVersion = String(response?.brandingVersion || '').trim();
    const previousBrandingVersion = brandingVersionRef.current;
    const hasBrandingChanged =
      !!nextBrandingVersion &&
      !!previousBrandingVersion &&
      nextBrandingVersion !== previousBrandingVersion;

    setBrandInfo({ icon: versionAssetUrl(faviconUrl, nextBrandingVersion), name: resolvedBrandName });
    // Persisting an optional canvas copy must not block manifest/brand readiness.
    void cacheBrandAssets(faviconUrl, resolvedBrandName, nextBrandingVersion);
    setFavicon(versionAssetUrl(faviconUrl, nextBrandingVersion) || faviconUrl);
    setBrandName(resolvedBrandName);
    updateFaviconLink(faviconUrl, nextBrandingVersion);

    let manifestPayload: unknown = response?.manifest;
    if (!manifestPayload && response?.manifest_url) {
      try {
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timeoutId = window.setTimeout(() => controller?.abort(), 3500);
        const manifestResponse = await fetch(versionAssetUrl(response.manifest_url, nextBrandingVersion), {
          cache: 'no-store',
          signal: controller?.signal,
        });
        window.clearTimeout(timeoutId);
        if (manifestResponse.ok) {
          manifestPayload = await manifestResponse.json();
        }
      } catch (manifestError) {
        console.warn("No se pudo cargar el manifest remoto", safeErrorDiagnostic(manifestError));
      }
    }

    const manifestData = buildManifestData(manifestPayload, resolvedBrandName, faviconUrl, nextBrandingVersion);
    const themeColor = manifestData?.theme_color || '#08111f';
    updateManifestLink(manifestData);
    ensureAppleTouchIcon(faviconUrl, nextBrandingVersion);

    if (typeof document !== 'undefined') {
      document.title = resolvedBrandName;
    }

    const setMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    setMetaTag('mobile-web-app-capable', 'yes');
    setMetaTag('apple-mobile-web-app-capable', 'yes');
    setMetaTag('apple-mobile-web-app-title', resolvedBrandName);
    setMetaTag('theme-color', themeColor);
    setMetaTag('apple-mobile-web-app-status-bar-style', 'black-translucent');
    setMetaTag('application-name', resolvedBrandName);

    if (nextBrandingVersion) {
      brandingVersionRef.current = nextBrandingVersion;
      setBrandingVersion(nextBrandingVersion);
      localStorage.setItem(BRAND_VERSION_KEY, nextBrandingVersion);
    } else {
      clearStoredBrandingVersion();
    }

    if (!hasBrandingChanged || !options?.allowSoftReload) {
      return;
    }

    if (options.tenantIdentifier) {
      ApiService.clearPublicBrandingCache(options.tenantIdentifier);
    }

    if (isIosDevice() && isStandalone) {
      const lastAcknowledgedVersion = localStorage.getItem(IOS_BRANDING_NOTICE_KEY) || '';
      if (lastAcknowledgedVersion !== nextBrandingVersion) {
        setShowIosBrandingNotice(true);
      }
      return;
    }

    window.setTimeout(() => {
      window.location.reload();
    }, 180);
  }, [BRAND_VERSION_KEY, IOS_BRANDING_NOTICE_KEY, cacheBrandAssets, clearStoredBrandingVersion, isIosDevice, isStandalone, setBrandName, setFavicon]);

  const markPwaSplashSeen = useCallback(() => {
    setHasSeenSplash(true);
    try {
      sessionStorage.setItem(SPLASH_SESSION_KEY, 'true');
    } catch {
      // ignore storage issues
    }
  }, []);

  const hideBootSplash = useCallback((markSeen = true) => {
    if (typeof window !== 'undefined') {
      const hide = (window as any).__ORGCLOUD_HIDE_BOOT_SPLASH__;
      if (typeof hide === 'function') {
        hide();
      }
      (window as any).__ORGCLOUD_BOOT_SPLASH_ACTIVE__ = false;
    }
    setBootSplashActive(false);
    if (markSeen) {
      markPwaSplashSeen();
    }
  }, [markPwaSplashSeen]);

  const canUseNotificationAPI = () =>
    typeof window !== 'undefined' &&
    'Notification' in window &&
    typeof Notification?.requestPermission === 'function';

  useEffect(() => {
    // Mostrar banner de permisos en cualquier plataforma si el usuario está logueado
    // (en iOS es obligatorio que el usuario haga click para que aparezca el prompt).
    if (isLocalPreview || !cookieConsentOk) {
      setShowBanner(false);
      return;
    }
    if (!user || !canUseNotificationAPI()) {
      setShowBanner(false);
      return;
    }
    if (isPushPromptDeclinedInCookie()) {
      setShowBanner(false);
      return;
    }
    const permission = Notification.permission;
    console.log("[Notifications] Estado del permiso:");
    if (permission === 'default') {
      setShowBanner(true);
    } else {
      setShowBanner(false);
    }
  }, [user, cookieConsentOk, isLocalPreview]);

  const askPermission = async () => {
    if (!canUseNotificationAPI()) {
      console.log('[Notifications] API de notificaciones no disponible');
      setShowBanner(false);
      return;
    }
    
    try {
      console.log('[Notifications] Solicitando permiso...');
      const permission = await Notification.requestPermission();
      console.log("[Notifications] Permiso obtenido:");
      
      // Siempre cerrar el banner después de solicitar el permiso,
      // independientemente de si el usuario lo acepta o lo rechaza
      setShowBanner(false);
      
      if (permission === "granted" && user) {
        console.log('[Notifications] Permiso concedido, suscribiendo a push...');
        // En iOS/PWA el permiso requiere gesto del usuario; suscribimos justo aquí.
        try {
          await subscribeToPush({ token: user, companyId: id });
          console.log('[Notifications] Suscripción completada exitosamente');
        } catch (error) {
          console.error("[Notifications] Error al suscribirse después de conceder permiso:", safeErrorDiagnostic(error));
        }
      } else {
        console.log('[Notifications] Permiso denegado o usuario no disponible');
      }
    } catch (error) {
      console.error("[Notifications] Error al solicitar permiso:", safeErrorDiagnostic(error));
      setShowBanner(false);
    }
  };

  usePushSubscription(user, id);

  const decodeToken = (token: string): TokenData | null => {
    try {
      if (!token || token.split('.').length !== 3) {
        throw new Error('Token inválido o malformado');
      }
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split('')
          .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
          .join('')
      );
      return JSON.parse(jsonPayload) as TokenData;
    } catch (error) {
      console.error("Error decoding token:", safeErrorDiagnostic(error));
      return null;
    }
  };

  useEffect(() => {
    clearLegacyBrandingKeys();
  }, [clearLegacyBrandingKeys]);

  useEffect(() => {
    let isMounted = true;
    const updateBranding = async () => {
      setIsBrandLoading(true);

      const tenantIdentifier = getTenantRoutingIdentifier();
      if (!tenantIdentifier || isMainDomain()) {
        if (isMounted) {
          setBrandInfo({ icon: Favicon, name: 'OrgCloud' });
          setFavicon(Favicon);
          setBrandName('OrgCloud');
          updateFaviconLink(Favicon);
          clearStoredBrandingVersion();
          setShowIosBrandingNotice(false);
          if (typeof document !== 'undefined') {
            document.title = 'OrgCloud';
          }
          setIsBrandLoading(false);
        }
        return;
      }

      try {
        const response = await Promise.race([
          ApiService.readBranding(tenantIdentifier, false, false),
          new Promise((_, reject) =>
            window.setTimeout(() => reject(new Error('branding_timeout')), 4000)
          ),
        ]);
        if (!isMounted) return;
        await applyResolvedBranding(response, { tenantIdentifier });
      } catch (error) {
        console.error("Error actualizando favicon y manifest:", safeErrorDiagnostic(error));
        if (isMounted) {
          const tenantFallback = getTenantFallbackBrandInfo();
          setBrandInfo(tenantFallback);
          setBrandName(tenantFallback.name || 'OrgCloud');
          setFavicon(tenantFallback.icon || '');
          updateFaviconLink(tenantFallback.icon || '');
          ensureAppleTouchIcon(tenantFallback.icon || '');
          if (typeof document !== 'undefined' && tenantFallback.name) {
            document.title = tenantFallback.name;
          }
        }
      } finally {
        if (isMounted) {
          setIsBrandLoading(false);
        }
      }
    };

    void updateBranding();

    return () => {
      isMounted = false;
    };
  }, [applyResolvedBranding, clearStoredBrandingVersion, setBrandName, setFavicon]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const tenantIdentifier = getTenantRoutingIdentifier();
    if (!tenantIdentifier || isMainDomain()) {
      return;
    }

    let cancelled = false;
    const checkLatestBranding = async () => {
      try {
        const latestBranding = await ApiService.readBranding(tenantIdentifier, false, false, { forceRefresh: true });
        if (cancelled) return;
        const latestVersion = String(latestBranding?.brandingVersion || '').trim();
        if (latestVersion && latestVersion !== brandingVersionRef.current) {
          await applyResolvedBranding(latestBranding, {
            allowSoftReload: true,
            tenantIdentifier,
          });
        }
      } catch (error) {
        console.warn("No se pudo comprobar la versión más reciente del branding", safeErrorDiagnostic(error));
      }
    };

    const handleVisibilityRefresh = () => {
      if (document.visibilityState === 'visible') {
        void checkLatestBranding();
      }
    };

    const refreshTimer = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void checkLatestBranding();
      }
    }, 5 * 60 * 1000);

    document.addEventListener('visibilitychange', handleVisibilityRefresh);
    window.addEventListener('focus', handleVisibilityRefresh);

    return () => {
      cancelled = true;
      window.clearInterval(refreshTimer);
      document.removeEventListener('visibilitychange', handleVisibilityRefresh);
      window.removeEventListener('focus', handleVisibilityRefresh);
    };
  }, [applyResolvedBranding]);

  useEffect(() => {
    if (!user && !isPublicAnalyticsRoute) return;

    const trackingSignature = [
      location.pathname,
      location.search,
      location.hash,
      id || '',
      user ? 'auth' : 'public',
    ].join('|');

    if (lastTrackedPageViewRef.current === trackingSignature) {
      return;
    }
    lastTrackedPageViewRef.current = trackingSignature;

    const firstSegment = location.pathname.split('/').filter(Boolean)[0] || 'root';
    const payload = {
      event: 'orgcloud.frontend.page_view',
      properties: buildPageViewAnalyticsProperties({
        route_segment: firstSegment,
        route_path: location.pathname,
        route_search: location.search,
        route_hash: location.hash,
        company_client_id: id || undefined,
        auth_state: user ? 'authenticated' : 'public_anonymous',
      }),
    };
    if (user) {
      FrontendAnalyticsService.captureSafe(user, payload);
    } else if (isPublicAnalyticsRoute) {
      FrontendAnalyticsService.capturePublicSafe(payload);
    }
  }, [id, isPublicAnalyticsRoute, location.hash, location.pathname, location.search, user]);

  const emitFrontendAnalyticsEvent = useCallback(
    (event: string, properties?: Record<string, unknown>) => {
      const payload = {
        event,
        properties: buildFrontendRuntimeProperties({
          company_client_id: id || undefined,
          auth_state: user ? 'authenticated' : 'public_anonymous',
          route_path: location.pathname,
          route_search: location.search,
          route_hash: location.hash,
          route_segment: location.pathname.split('/').filter(Boolean)[0] || 'root',
          ...properties,
        }),
      };
      if (user) {
        FrontendAnalyticsService.captureSafe(user, payload);
        return;
      }
      if (isPublicAnalyticsRoute) {
        FrontendAnalyticsService.capturePublicSafe(payload);
      }
    },
    [id, isPublicAnalyticsRoute, location.hash, location.pathname, location.search, user]
  );

  useEffect(() => {
    if ((!user && !isPublicAnalyticsRoute) || frontendSessionStartedRef.current) return;
    frontendSessionStartedRef.current = true;
    emitFrontendAnalyticsEvent('orgcloud.frontend.session_started', {
      entry_path: location.pathname,
      entry_search: location.search,
    });
  }, [emitFrontendAnalyticsEvent, isPublicAnalyticsRoute, location.pathname, location.search, user]);

  useEffect(() => {
    if (!user && !isPublicAnalyticsRoute) return;

    routeAccumulatedVisibleMsRef.current = 0;
    routeInteractionCountRef.current = 0;
    routeScrollMilestonesRef.current = new Set();
    firstInteractionSentRef.current = false;
    routeVisibleStartedAtRef.current =
      typeof document !== 'undefined' && document.visibilityState === 'visible' ? Date.now() : null;

    const flushEngagement = (reason: string) => {
      const now = Date.now();
      if (routeVisibleStartedAtRef.current) {
        routeAccumulatedVisibleMsRef.current += now - routeVisibleStartedAtRef.current;
        routeVisibleStartedAtRef.current = null;
      }
      emitFrontendAnalyticsEvent('orgcloud.frontend.page_engagement', {
        reason,
        visible_ms: routeAccumulatedVisibleMsRef.current,
        interaction_count: routeInteractionCountRef.current,
        max_scroll_depth_percent: Math.max(0, ...Array.from(routeScrollMilestonesRef.current.values())),
        bounced: routeInteractionCountRef.current === 0,
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        routeVisibleStartedAtRef.current = Date.now();
        return;
      }
      flushEngagement('visibility_hidden');
    };

    const handlePageHide = () => flushEngagement('pagehide');

    const handleScroll = () => {
      if (typeof window === 'undefined' || typeof document === 'undefined') return;
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const percent = Math.round((window.scrollY / scrollable) * 100);
      [25, 50, 75, 100].forEach((threshold) => {
        if (percent < threshold || routeScrollMilestonesRef.current.has(threshold)) return;
        routeScrollMilestonesRef.current.add(threshold);
        emitFrontendAnalyticsEvent('orgcloud.frontend.scroll_depth', {
          scroll_depth_percent: threshold,
        });
      });
    };

    const heartbeat = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      const visibleMs =
        routeAccumulatedVisibleMsRef.current +
        (routeVisibleStartedAtRef.current ? now - routeVisibleStartedAtRef.current : 0);
      emitFrontendAnalyticsEvent('orgcloud.frontend.session_heartbeat', {
        visible_ms: visibleMs,
      });
    }, 60000);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.clearInterval(heartbeat);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('scroll', handleScroll);
      flushEngagement('route_change');
    };
  }, [emitFrontendAnalyticsEvent, isPublicAnalyticsRoute, location.key, location.pathname, user]);

  useEffect(() => {
    if (!user && !isPublicAnalyticsRoute) return;

    const handleClick = (event: MouseEvent) => {
      const details = describeInteractionTarget(event.target);
      if (!details) return;
      const signature = JSON.stringify([
        details.action_kind,
        details.analytics_id,
        details.element_label,
        details.href,
      ]);
      const now = Date.now();
      if (
        signature === lastInteractionSignatureRef.current &&
        now - lastInteractionTimestampRef.current < 800
      ) {
        return;
      }
      lastInteractionSignatureRef.current = signature;
      lastInteractionTimestampRef.current = now;
      routeInteractionCountRef.current += 1;
      if (!firstInteractionSentRef.current) {
        firstInteractionSentRef.current = true;
        emitFrontendAnalyticsEvent('orgcloud.frontend.first_interaction', {
          action_kind: details.action_kind,
          target: details.target,
          component: details.component,
        });
      }
      emitFrontendAnalyticsEvent('orgcloud.frontend.interaction', details);
    };

    const handleSubmit = (event: SubmitEvent) => {
      const details = describeFormSubmission(event.target);
      if (!details) return;
      emitFrontendAnalyticsEvent('orgcloud.frontend.form_submit', details);
    };

    const handleWindowError = (event: ErrorEvent) => {
      emitFrontendAnalyticsEvent('orgcloud.frontend.window_error', {
        message: compactAnalyticsError(event.error || event.message),
        source_file: diagnosticSourceFile(event.filename),
        line: event.lineno || undefined,
        column: event.colno || undefined,
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      emitFrontendAnalyticsEvent('orgcloud.frontend.unhandled_rejection', {
        message: compactAnalyticsError(event.reason),
      });
    };

    document.addEventListener('click', handleClick, true);
    document.addEventListener('submit', handleSubmit, true);
    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('submit', handleSubmit, true);
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [emitFrontendAnalyticsEvent, isPublicAnalyticsRoute, location.pathname, user]);

  useEffect(() => {
    if (!user && !isPublicAnalyticsRoute) return;

    const handleApiError = (event: Event) => {
      const detail = (event as CustomEvent<Record<string, unknown>>).detail || {};
      emitFrontendAnalyticsEvent('orgcloud.frontend.api_error', detail);
    };

    window.addEventListener('orgcloud:api-error', handleApiError as EventListener);
    return () => {
      window.removeEventListener('orgcloud:api-error', handleApiError as EventListener);
    };
  }, [emitFrontendAnalyticsEvent, isPublicAnalyticsRoute, user]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleBeforeInstallPrompt = (event: any) => {
      event.preventDefault();
      setInstallPromptEvent(event);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [IOS_BRANDING_NOTICE_KEY]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQueries = ['standalone', 'fullscreen'].map((mode) =>
      window.matchMedia(`(display-mode: ${mode})`)
    );
    const updateStandalone = () => {
      setIsStandalone(isInstalledAppRuntime());
      setIsMobileStandaloneExperience(isMobileStandaloneSplashDevice());
    };
    updateStandalone();

    window.addEventListener('resize', updateStandalone);
    mediaQueries.forEach((query) => query.addEventListener('change', updateStandalone));
    return () => {
      window.removeEventListener('resize', updateStandalone);
      mediaQueries.forEach((query) => query.removeEventListener('change', updateStandalone));
    };
  }, []);

  useEffect(() => {
    if (bootSplashActive) {
      setShowSplash(false);
      return;
    }

    if (!isStandalone || !isMobileStandaloneExperience || hasSeenSplash) {
      setShowSplash(false);
      return;
    }

    if (isBrandLoading) {
      setShowSplash(true);
      return;
    }

    const timer = window.setTimeout(() => {
      setShowSplash(false);
      markPwaSplashSeen();
    }, 800);

    return () => window.clearTimeout(timer);
  }, [bootSplashActive, isBrandLoading, isMobileStandaloneExperience, isStandalone, hasSeenSplash, markPwaSplashSeen]);

  useEffect(() => {
    if (!bootSplashActive) {
      // Browser-mode launches also have an HTML loading state before React.
      const hide = (window as any).__ORGCLOUD_HIDE_BOOT_SPLASH__;
      if (typeof hide === 'function') hide();
      return undefined;
    }

    if (!isStandalone || !isMobileStandaloneExperience) {
      hideBootSplash(false);
      return undefined;
    }

    // React has committed its route/loading state. Cosmetic branding must not
    // keep the pre-JavaScript overlay above an otherwise usable login.
    hideBootSplash(true);
    return undefined;
  }, [bootSplashActive, hideBootSplash, isMobileStandaloneExperience, isStandalone]);

  useEffect(() => {
    if (!bootSplashActive) return undefined;

    const fallbackTimer = window.setTimeout(() => {
      setIsBrandLoading(false);
      hideBootSplash(true);
    }, 3600);

    return () => window.clearTimeout(fallbackTimer);
  }, [bootSplashActive, hideBootSplash]);

  useEffect(() => {
    if (bootSplashActive || !isStandalone || !isMobileStandaloneExperience || hasSeenSplash || !showSplash) {
      return;
    }

    const fallbackTimer = window.setTimeout(() => {
      setShowSplash(false);
      setIsBrandLoading(false);
      markPwaSplashSeen();
    }, 3200);

    return () => window.clearTimeout(fallbackTimer);
  }, [bootSplashActive, hasSeenSplash, isMobileStandaloneExperience, isStandalone, markPwaSplashSeen, showSplash]);

  const handleInstallApp = async () => {
    if (!installPromptEvent) return;
    installPromptEvent.prompt();
    await installPromptEvent.userChoice;
    setInstallPromptEvent(null);
  };

  const dismissIosBrandingNotice = useCallback(() => {
    if (brandingVersionRef.current) {
      localStorage.setItem(IOS_BRANDING_NOTICE_KEY, brandingVersionRef.current);
    }
    setShowIosBrandingNotice(false);
  }, [IOS_BRANDING_NOTICE_KEY]);

  // ✅ Valida ID token: si caducó pero hay "recordarme" + refresh, renueva antes de expulsar (PWA / pestañas largas).
  // ✅ Renueva también en ventana previa a exp para reducir 401 y redirecciones.
  useEffect(() => {
    let cancelled = false;
    /** En navegador es `number`; evita choque con `NodeJS.Timeout` de @types/node. */
    let proactiveTimer: number | undefined;
    const REFRESH_WINDOW_SEC = 4 * 60; // 4 min antes de exp

    const run = async () => {
      if (!user) {
        console.warn('No hay token en el contexto de usuario');
        setIsExpired(true);
        return;
      }

      if (typeof user !== 'string' || user.split('.').length !== 3) {
        console.warn('Token no disponible o mal formado');
        setIsExpired(true);
        return;
      }

      const decodedToken = decodeToken(user);
      if (!decodedToken?.exp) {
        console.error('Token no válido o sin campo exp');
        setIsExpired(true);
        return;
      }

      const now = Math.floor(Date.now() / 1000);
      const expired = idTokenCalendarExpired(decodedToken.exp, now);
      const inWindow = idTokenWithinRefreshWindow(decodedToken.exp, now, REFRESH_WINDOW_SEC);

      if (!expired) {
        setIsExpired(false);
        if (!TokenManager.shouldAttemptRefresh()) {
          return;
        }
        const secondsLeft = decodedToken.exp - now;
        if (inWindow) {
          if (sessionRefreshInFlightRef.current) return;
          sessionRefreshInFlightRef.current = true;
          try {
            await TokenManager.refreshSession();
          } catch (e) {
            console.warn('[Session] Renovación proactiva (ventana pre-exp) fallida:', safeErrorCode(e));
          } finally {
            sessionRefreshInFlightRef.current = false;
          }
          return;
        }
        const delayMs = Math.max(0, (secondsLeft - REFRESH_WINDOW_SEC) * 1000);
        if (delayMs < 48 * 60 * 60 * 1000) {
          proactiveTimer = window.setTimeout(() => {
            if (cancelled || sessionRefreshInFlightRef.current) return;
            sessionRefreshInFlightRef.current = true;
            void TokenManager.refreshSession()
              .catch((e) => console.warn('[Session] Renovación proactiva programada fallida:', safeErrorCode(e)))
              .finally(() => {
                sessionRefreshInFlightRef.current = false;
              });
          }, delayMs);
        }
        return;
      }

      if (!TokenManager.shouldAttemptRefresh()) {
        setIsExpired(true);
        return;
      }

      if (sessionRefreshInFlightRef.current) {
        // Otra renovación en curso (p. ej. proactiva): no redirigir a login mientras tanto.
        setIsExpired(false);
        return;
      }

      sessionRefreshInFlightRef.current = true;
      try {
        setIsExpired(false);
        await TokenManager.refreshSession();
      } catch (e) {
        console.error('[Session] Renovación silenciosa (token caducado) fallida:', safeErrorCode(e));
        if (!cancelled) {
          setIsExpired(true);
        }
      } finally {
        sessionRefreshInFlightRef.current = false;
      }
    };

    void run();
    return () => {
      cancelled = true;
      if (proactiveTimer !== undefined) {
        window.clearTimeout(proactiveTimer);
      }
    };
  }, [user]);

  // ✅ Procesar hash en la URL (para compatibilidad con enlaces que usan hash)
  // Si la URL tiene un hash que parece una ruta, navegar a esa ruta sin hash
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.startsWith('#/')) {
      const hashPath = hash.substring(1); // Remover el #
      const currentPath = window.location.pathname;
      
      // Solo navegar si el pathname actual no coincide con el hash
      if (currentPath !== hashPath) {
        console.log("🔄 App: Detectado hash en URL, navegando a ruta:");
        navigate(hashPath, { replace: true });
      }
    }
  }, [navigate]);

  // ✅ Redirección automática si no hay usuario o token expirado
  // NOTA: Esta lógica se maneja principalmente en LandingRedirect y AuthWithLandingCheck
  // Este useEffect solo maneja casos especiales
  useEffect(() => {
    const path = window.location.pathname;
    const isPublicRoute = isPublicPath(path, window.location.hash);
    const isAuthRoute = path.startsWith('/auth/');

    // CRÍTICO: Las rutas públicas NO deben ser redirigidas bajo ninguna circunstancia
    // Las rutas públicas deben ser accesibles sin autenticación
    if (isPublicRoute) {
      console.log("✅ Main: Ruta pública detectada, no redirigiendo ni verificando autenticación:");
      return;
    }

    if (isAuthRoute) {
      console.log("✅ Main: Ruta auth, no redirigiendo:");
      return;
    }

    // Si no hay usuario y no estamos en una ruta pública/auth, 
    // LandingRedirect se encargará de la redirección
    if (!user && !isPublicRoute && !isAuthRoute) {
      // LandingRedirect manejará la redirección
      return;
    }

    // Solo redirigir si el token está expirado y no estamos en una ruta pública
    if (isExpired && !isPublicRoute && !isAuthRoute) {
      navigate('/auth/sign-in/', { replace: true });
    }
  }, [isExpired, user, navigate]);

  const cookieConsentBanner =
    !isLocalPreview && !cookieConsentOk ? (
      <CookieConsentBanner
        privacyPolicyUrl={privacyPolicyUrl}
        onAccept={() => {
          recordCookieConsent();
          setCookieConsentOk(true);
        }}
      />
    ) : null;
  const routeParams = new URLSearchParams(location.search);
  const shouldOpenLoginForMobilePwa =
    location.pathname === '/' &&
    (routeParams.get('pwa') === '1' || routeParams.has('reset') || (isStandalone && isMobileStandaloneExperience));

  // Mostrar loading mientras se verifica la landing page (solo en carga inicial sin usuario)
  // CRÍTICO: Las rutas públicas NO deben mostrar loading ni verificación
  if (isPublicPath(location.pathname, location.hash)) {
    // Permitir que las rutas públicas se rendericen inmediatamente sin verificación
    // Continuar con el render normal
  }

  return (
    <ChakraProvider theme={currentTheme} toastOptions={orgcloudGlassToastOptions}>
      <ScrollToTopOnRouteChange />
      <DocumentOverscrollBackground />
      {showIosBrandingNotice ? (
        <Box
          data-branding-version={brandingVersion || undefined}
          position="fixed"
          top="max(env(safe-area-inset-top, 0px) + 12px, 12px)"
          left="50%"
          transform="translateX(-50%)"
          zIndex={1700}
          w="min(92vw, 680px)"
          bg="rgba(10, 14, 24, 0.82)"
          border="1px solid rgba(255, 255, 255, 0.16)"
          backdropFilter="blur(20px)"
          boxShadow="0 24px 60px rgba(15, 23, 42, 0.28)"
          borderRadius="24px"
          px={{ base: 4, md: 5 }}
          py={{ base: 3.5, md: 4 }}
          color="white"
        >
          <HStack align="start" spacing={4} justify="space-between">
            <Box flex="1">
              <Text fontSize={{ base: 'sm', md: 'md' }} fontWeight="700" mb="1">
                {iosBrandingNoticeCopy.title}
              </Text>
              <Text fontSize={{ base: 'xs', md: 'sm' }} color="whiteAlpha.800" lineHeight="1.6">
                {iosBrandingNoticeCopy.body}
              </Text>
            </Box>
            <Button
              size="sm"
              flexShrink={0}
              borderRadius="full"
              bg="whiteAlpha.220"
              color="white"
              _hover={{ bg: 'whiteAlpha.320' }}
              _active={{ bg: 'whiteAlpha.360' }}
              onClick={dismissIosBrandingNotice}
            >
              {iosBrandingNoticeCopy.action}
            </Button>
          </HStack>
        </Box>
      ) : null}
      <PWASplashScreen
        isVisible={showSplash && isMobileStandaloneExperience}
        iconSrc={brandInfo.icon}
        brandName={brandInfo.name}
        installAvailable={!!installPromptEvent}
        onInstallClick={handleInstallApp}
        showInstallHint={!isStandalone}
      />
      <PWAInstallCoachmark enabled={!isLocalPreview && !bootSplashActive && !(showSplash && isMobileStandaloneExperience)} />
      {cookieConsentBanner}
      {!isLocalPreview && showBanner ? (
        <PushNotificationPrompt
          onEnable={askPermission}
          onClose={() => {
            console.log('[Notifications] Usuario cerró o rechazó el aviso (cookie si aplica)');
            setShowBanner(false);
          }}
        />
      ) : null}
      <Suspense fallback={<AppRouteSkeleton />}>
      <Routes>
        {/* Rutas públicas - deben ser accesibles sin autenticación */}
        <Route path="public/reservations" element={<PublicReservations />} />
        <Route path="public/reservations/:clientId" element={<PublicReservations />} />
        <Route path="public/resource/:resourceType/:resourceId" element={<PublicSportEventPage mode="resource" />} />
        <Route path="r/:resourceType/:resourceId" element={<PublicSportEventPage mode="resource" />} />
        <Route path="event/:eventId" element={<PublicSportEventPage mode="event" />} />
        <Route path="e/:eventId" element={<PublicSportEventPage mode="event" />} />
        <Route path="competition/:competitionId" element={<PublicSportEventPage mode="competition" />} />
        <Route path="c/:competitionId" element={<PublicSportEventPage mode="competition" />} />
        <Route path="club/:memberId" element={<PublicMemberProfile />} />
        <Route path="instructor/:memberId" element={<PublicMemberProfile />} />
        <Route path="profile/:memberId" element={<PublicMemberProfile />} />
        <Route path="p/:memberId" element={<PublicMemberProfile />} />
        <Route
          path="public/*"
          element={
            <PublicLayout
              title=""
              routes={publicRoutes}
              theme={currentTheme}
              setTheme={setCurrentTheme}
            />
          }
        />
        <Route path="ticket/:objectid/:ticketid" element={<PublicPurchaseReceipt />} />
        
        {/* Rutas de autenticación */}
        <Route 
          path="auth/*" 
          element={
            <AuthWithLandingCheck>
              <AuthLayout />
            </AuthWithLandingCheck>
          } 
        />

        {/* Rutas protegidas - solo para usuarios autenticados */}
        {user && !isExpired ? (
          <>
            <Route path="admin/pages/*" element={<LegacyAdminSectionRedirect />} />
            <Route path="admin/tools/*" element={<LegacyAdminSectionRedirect />} />
            <Route path="admin/objects/*" element={<LegacyAdminSectionRedirect />} />
            <Route path="admin/bidireader" element={<LegacyAppRedirect to="/tools/bidireader" />} />
            <Route path="admin/reservations" element={<LegacyAppRedirect to="/tools/reservations" />} />
            <Route path="admin/management/mapmanagement" element={<LegacyAppRedirect to="/tools/mapmanagement" />} />
            <Route path="admin/management/storemanagement" element={<LegacyAppRedirect to="/tools/storemanagement" />} />
            <Route path="pages/page/*" element={<LegacyPagesPageRedirect />} />
            <Route
              path="pages/*"
              element={<AdminLayout theme={currentTheme} setTheme={setCurrentTheme} />}
            />
            <Route
              path="tools/*"
              element={<AdminLayout theme={currentTheme} setTheme={setCurrentTheme} />}
            />
            <Route
              path="objects/*"
              element={<AdminLayout theme={currentTheme} setTheme={setCurrentTheme} />}
            />
            <Route
              path="admin/*"
              element={<AdminLayout theme={currentTheme} setTheme={setCurrentTheme} />}
            />
            <Route
              path="rtl/*"
              element={<RTLLayout theme={currentTheme} setTheme={setCurrentTheme} />}
            />
          </>
        ) : null}

        {/* Ruta catch-all solo para rutas que NO son públicas ni de auth */}
        {/* Las rutas públicas ya están manejadas arriba, así que esto solo captura otras rutas */}
        <Route path="*" element={<ConditionalLandingRedirect />} />

        {/* Ruta raíz */}
        <Route
          path="/"
          element={
            user && !isExpired ? (
              <Navigate to="/admin" replace />
            ) : shouldOpenLoginForMobilePwa ? (
              <Navigate to="/auth/sign-in/" replace />
            ) : (
              <ConditionalLandingRedirect />
            )
          }
        />
      </Routes>
      </Suspense>
    </ChakraProvider>
  );
}
